import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Modal, Button, Form } from 'react-bootstrap';
import axios from '../axiosAlertConfig';

const text = value => String(value == null ? '' : value).trim();
const errorMessage = error => (error?.response || error)?.data?.error || error?.message || 'The location request failed.';

export default function UpdateLocationModal({ show, onHide, incident, userContext, onSaved }) {
  const user = userContext?.user || {};
  // A different SchoolShield tab / the user's default campus must not change
  // the location list for the incident being viewed.
  const account = text(incident?.iAccountID || userContext?.iAccountID);
  const building = text(incident?.iBuildingID || userContext?.iBuildingID);
  const scope = useMemo(() => ({ iAccountID: account, iBuildingID: building }), [account, building]);
  const scopeKey = JSON.stringify([account, building, incident?.incidentID]);
  const currentScope = useRef(scopeKey); currentScope.current = scopeKey;
  const alive = useRef(false); const requests = useRef(0); const touched = useRef(false); const saving = useRef(false);
  const savedLocation = useRef(''); savedLocation.current = text(incident?.myLocation);
  const [state, setState] = useState({ scope: '', rows: [] });
  const [selected, setSelected] = useState(''); const [otherText, setOtherText] = useState('');
  const [loading, setLoading] = useState(false); const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const locations = state.scope === scopeKey ? state.rows : [];
  useEffect(() => { alive.current = true; return () => { alive.current = false; requests.current += 1; }; }, []);
  const load = useCallback(async () => {
    const version = ++requests.current;
    if (!show || !account || !building) { setState({ scope: scopeKey, rows: [] }); return; }
    setLoading(true); setListError('');
    try {
      const { data } = await axios.get('/userlocations', { params: scope });
      if (!Array.isArray(data)) throw new Error('The server did not return a location list.');
      if (!alive.current || version !== requests.current || currentScope.current !== scopeKey) return;
      const rows = data.filter(row => text(row.iBuildingID) === building && text(row.location));
      setState({ scope: scopeKey, rows });
      if (!touched.current && savedLocation.current && !rows.some(row => row.location === savedLocation.current)) {
        setSelected('__OTHER__'); setOtherText(savedLocation.current);
      }
    } catch (err) {
      if (alive.current && version === requests.current && currentScope.current === scopeKey) {
        setState({ scope: scopeKey, rows: [] }); setListError(errorMessage(err));
        if (!touched.current && savedLocation.current) { setSelected('__OTHER__'); setOtherText(savedLocation.current); }
      }
    } finally { if (alive.current && version === requests.current && currentScope.current === scopeKey) setLoading(false); }
  }, [show, account, building, scope, scopeKey]);
  useEffect(() => {
    touched.current = false; setSelected(savedLocation.current); setOtherText(''); setError('');
    setState({ scope: scopeKey, rows: [] }); setLoading(false);
    setListError('');
    if (show) void load();
    const refresh = () => { if (show && !saving.current) void load(); };
    window.addEventListener('focus', refresh);
    window.addEventListener('schoolshield:locations-changed', refresh);
    return () => { requests.current += 1; window.removeEventListener('focus', refresh); window.removeEventListener('schoolshield:locations-changed', refresh); };
  }, [show, scopeKey, load]);

  async function handleSave() {
    const loc = text(selected === '__OTHER__' ? otherText : selected);
    if (saving.current || !loc || !account || !building || !incident?.incidentID) return;
    saving.current = true; setBusy(true); setError('');
    try {
      await axios.post(`/ialert/incidentmessages/${incident.incidentID}`, {
        iAccountID: account, iCampusID: incident.iCampusID || '-99', iBuildingID: building,
        senderUserID: user.userID, senderName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        senderMobileNumber: user.userMobileNumber || '', messageText: `(updated location) ${loc}`, myLocation: loc,
      });
      if (alive.current && currentScope.current === scopeKey) { onSaved(loc); onHide(); }
    } catch (err) { if (alive.current && currentScope.current === scopeKey) setError(errorMessage(err)); }
    finally { saving.current = false; if (alive.current) setBusy(false); }
  }
  return <Modal show={show} onHide={() => { if (!busy) onHide(); }} centered>
    <Modal.Header closeButton={!busy}><Modal.Title>Update My Location</Modal.Title></Modal.Header>
    <Modal.Body>
      <p className="small text-muted mb-2">{incident?.buildingName || 'Active building'} — shared locations and homerooms.</p>
      {(!account || !building) && <Alert variant="warning">The incident building is unavailable. Reopen the incident before saving a location.</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      {listError && <Alert variant="warning">{listError}<div>The location list is unavailable. You may still enter your actual location using Other.</div><Button variant="link" size="sm" onClick={load} disabled={loading || busy}>Retry list</Button></Alert>}
      <Form.Group><Form.Label>Select Location</Form.Label>
        <Form.Select value={selected} disabled={busy} onChange={event => { touched.current = true; setSelected(event.target.value); }}>
          <option value="">{loading ? 'Loading locations…' : '-- choose --'}</option>
          {selected && selected !== '__OTHER__' && !locations.some(row => row.location === selected) && <option value={selected} disabled>{selected} (current saved location)</option>}
          {locations.map(row => <option key={row.key || `${building}:${row.location}`} value={row.location}>{row.location}</option>)}
          <option value="__OTHER__">Other…</option>
        </Form.Select>
      </Form.Group>
      {selected === '__OTHER__' && <Form.Group className="mt-3"><Form.Label>Enter location</Form.Label><Form.Control type="text" maxLength={255} value={otherText} disabled={busy} onChange={event => { touched.current = true; setOtherText(event.target.value); }} /></Form.Group>}
    </Modal.Body>
    <Modal.Footer><Button variant="secondary" disabled={busy} onClick={onHide}>Cancel</Button><Button onClick={handleSave} disabled={busy || !account || !building || !text(selected === '__OTHER__' ? otherText : selected)}>{busy ? 'Saving…' : 'Save'}</Button></Modal.Footer>
  </Modal>;
}
